import type { TCatFormDataInput, TFieldKey } from "@./suf-store";
import { SIGNUP_FLOW_PAGES, SignupFlowSlug } from "../constants";
import type { RequiredFieldData } from "../../../shared/baseStore.types";

import { requiredFieldsBySlug } from "./requiredFieldsBySlug";

/**
 * Slugs whose fields are critical for downstream data correctness.
 *
 * A field is critical if its absence causes incorrect subscription data
 * (wrong portion sizes, tray size, calorie target), fallback API values
 * that misrepresent the cat, or incorrect recipe recommendations.
 * Email (cat parent details) is also critical as it gates access to recipes.
 *
 * Non-critical pages (e.g. eating habits) have safe fallbacks and do not
 * break subscription creation or calorie calculations when missing.
 *
 * When adding a new page to the SUF, classify it here as `true` (critical) or
 * `false` (non-critical). The `Record` type makes this a compile error if missed.
 */
const IS_CRITICAL_SLUG: Record<SignupFlowSlug, boolean> = {
  [SignupFlowSlug.CAT_COUNT]: false,
  [SignupFlowSlug.BASIC_INFORMATION]: true,
  [SignupFlowSlug.AGE]: true,
  [SignupFlowSlug.EATING_HABITS]: false,
  [SignupFlowSlug.FLAVOURS]: false,
  [SignupFlowSlug.TEXTURES]: false,
  [SignupFlowSlug.FITNESS]: true,
  [SignupFlowSlug.WEIGHT]: true,
  [SignupFlowSlug.CURRENT_DIET]: false,
  [SignupFlowSlug.MEDICAL_CONDITIONS]: false,
  [SignupFlowSlug.ALLERGIES]: false,
  [SignupFlowSlug.MOTIVATION]: false,
  [SignupFlowSlug.CAT_PARENT_DETAILS]: true,
  [SignupFlowSlug.CHECKOUT]: false,
  [SignupFlowSlug.PRICING]: false,
  [SignupFlowSlug.RECIPES]: false,
  [SignupFlowSlug.WHY_FRESH]: false,
  [SignupFlowSlug.TREATS]: false,
};
export const CRITICAL_SLUGS = new Set<SignupFlowSlug>(
  Object.values(SignupFlowSlug)
    .filter((it) => IS_CRITICAL_SLUG[it]),
);

/**
 * Returns critical fields for a given slug, expanding per-cat fields for cat
 * pages (mirroring the same expansion logic used by `getRequiredFieldsForSlug`).
 *
 * Delegates to `requiredFieldsBySlug` for field definitions so there is a
 * single source of truth for which fields each page requires.
 */
export function getCriticalFieldsForSlug(
  slug: SignupFlowSlug,
  cats: Record<string, TCatFormDataInput>,
  context?: Pick<RequiredFieldData, "inABTestGroup">,
): TFieldKey[] {
  if (!CRITICAL_SLUGS.has(slug)) {
    return [];
  }

  const getFields = requiredFieldsBySlug[slug];
  const isUserPage = SIGNUP_FLOW_PAGES[slug]?.isUserPage ?? false;
  if (isUserPage) {
    return getFields({ inABTestGroup: context?.inABTestGroup });
  }

  const fields: TFieldKey[] = [];
  for (const [ catId, cat ] of Object.entries(cats)) {
    if (!cat.softDelete) {
      fields.push(...getFields({ cat, catId, inABTestGroup: context?.inABTestGroup }));
    }
  }
  return fields;
}
